import {
    SimpleAndFilter,
    SimpleOrFilter,
} from "@impero-com/common/utils/indexing";
import { assertNever } from "@impero-com/common/utils/obj";

import { ReportsStatusColumn, ReportsStatusFilters } from "api/generated/model";
import { PageStateData } from "org/pageState/models";
import { ReportColumnId, REPORT_COLUMN_IDS } from "org/reports/list/columns";

import { decodeOrFilter, encodeOrFilter } from "../common";

import { decodeAccessRights, encodeAccessRights } from "./common";

import { ReportsPageStateRaw } from ".";

type ReportsStatusPageStateWithType = PageStateData<"ReportsStatus"> & {
    type: "ReportsStatus";
};

function encodeReportsStatusPageState(
    raw: ReportsPageStateRaw
): ReportsStatusPageStateWithType {
    if (raw.pageType !== "ReportsStatus") {
        throw new Error(
            `Expected page type to be "ReportsStatus", but got "${raw.pageType}"`
        );
    }

    return {
        type: "ReportsStatus",
        version: raw.version,
        columns: Array.from(raw.visibleColumnIds).map(
            encodeReportsStatusColumn
        ),
        filters: encodeReportsStatusFilters(raw.filter),
        leftSideMenuCollapsed: raw.leftSideMenuCollapsed,
    };
}

function decodeReportsStatusPageState(
    input: ReportsStatusPageStateWithType
): ReportsPageStateRaw {
    if (input.type !== "ReportsStatus") {
        throw new Error(
            `Expected page type to be "ReportsStatus", but got "${input.type}"`
        );
    }

    const visibleColumnIdsAsArray = input.columns.map(
        decodeReportsStatusColumn
    );

    return {
        pageType: "ReportsStatus",
        version: input.version,
        visibleColumnIds: new Set(visibleColumnIdsAsArray),
        filter: decodeReportsStatusFilters(input.filters),
        leftSideMenuCollapsed: input.leftSideMenuCollapsed,
    };
}

function encodeReportsStatusColumn(
    rawColumn: ReportColumnId
): ReportsStatusColumn {
    switch (rawColumn) {
        case REPORT_COLUMN_IDS.NAME:
            return "name";
        case REPORT_COLUMN_IDS.DESCRIPTION:
            return "description";
        case REPORT_COLUMN_IDS.PUSH_REPORTS_COUNT:
            return "pushReportsCount";
        case REPORT_COLUMN_IDS.ACCESS_RIGHTS:
            return "accessRights";
        case REPORT_COLUMN_IDS.ACTION:
            return "actions";
        case REPORT_COLUMN_IDS.NAME_FOR_ALL:
        case REPORT_COLUMN_IDS.DATASHEET:
        case REPORT_COLUMN_IDS.DATASHEET_ARCHIVED:
        case REPORT_COLUMN_IDS.CONTROL_PROGRAM:
        case REPORT_COLUMN_IDS.CLASSIFICATION:
        case REPORT_COLUMN_IDS.REPORT_TYPE:
            throw new Error(
                `Unknown column for ReportsStatus page: ${rawColumn}`
            );
        default:
            return assertNever(rawColumn);
    }
}

function decodeReportsStatusColumn(
    column: ReportsStatusColumn
): ReportColumnId {
    switch (column) {
        case "name":
            return REPORT_COLUMN_IDS.NAME;
        case "description":
            return REPORT_COLUMN_IDS.DESCRIPTION;
        case "pushReportsCount":
            return REPORT_COLUMN_IDS.PUSH_REPORTS_COUNT;
        case "accessRights":
            return REPORT_COLUMN_IDS.ACCESS_RIGHTS;
        case "actions":
            return REPORT_COLUMN_IDS.ACTION;
        default:
            return assertNever(column);
    }
}

function encodeReportsStatusFilters(
    filter: SimpleAndFilter | null
): ReportsStatusFilters {
    if (filter === null) {
        return {};
    }

    const filters = filter.filters;
    return {
        name: encodeOrFilter(REPORT_COLUMN_IDS.NAME, filters),
        description: encodeOrFilter(REPORT_COLUMN_IDS.DESCRIPTION, filters),
        pushReportsCount: encodeOrFilter(
            REPORT_COLUMN_IDS.PUSH_REPORTS_COUNT,
            filters
        ),
        accessRights: encodeAccessRights(filters),
    };
}

function decodeReportsStatusFilters(
    filters: ReportsStatusFilters
): SimpleAndFilter | null {
    if (Object.keys(filters).length === 0) {
        return null;
    }

    const orFilters: Array<SimpleOrFilter> = [];
    if (filters.name) {
        orFilters.push(decodeOrFilter(REPORT_COLUMN_IDS.NAME, filters.name));
    }
    if (filters.description) {
        orFilters.push(
            decodeOrFilter(REPORT_COLUMN_IDS.DESCRIPTION, filters.description)
        );
    }
    if (filters.pushReportsCount) {
        orFilters.push(
            decodeOrFilter(
                REPORT_COLUMN_IDS.PUSH_REPORTS_COUNT,
                filters.pushReportsCount
            )
        );
    }
    if (filters.accessRights) {
        const accessRightFilters = decodeAccessRights(filters.accessRights);
        orFilters.push(...accessRightFilters);
    }

    return {
        filters: orFilters,
    };
}

export { encodeReportsStatusPageState, decodeReportsStatusPageState };

export type { ReportsStatusPageStateWithType };
